import { useContext } from "react";
import { AuthContext } from "@/context/AuthContext";
import { useMosaic } from "@/context/mosaic/MosaicContext";
import { useTabs } from "@/context/tabs/TabsContext";
import { cn } from "@/lib/utils";
import {
	COMING_SOON_WIDGETS,
	type UserRole,
	UserRoles,
	WIDGETS_BY_ROLE,
} from "@/services/user-auth/user-roles";
import { HoverCardWrapper } from "@/shared/components/HoverCardWrapper";
import { OfferToUpgrade } from "@/shared/components/OfferToUpgrade";
import { isWidgetOpened } from "@/shared/helpers/isWidgetOpened";
import type { ViewId } from "@/shared/types/common";
import sprites from "/src/shared/assets/sprite.svg";
import { ChatIconWithPhoneForm } from "../../shared/components/ChatIconWithPhoneForm";
import {
	Menubar,
	MenubarContent,
	MenubarItem,
	MenubarMenu,
	MenubarPortal,
	MenubarTrigger,
} from "../ui/menubar";
import { mapOfferMessage } from "./consts";
import { getWidgetColorClassName } from "./helpers/getWidgetColorClassName";
import { menuItems } from "./MenuItems/MenuItems";
import type { MenuItem } from "./MenuItemTypes";

const SUBSCRIPTION_ROLES = [
	UserRoles.BRONZE,
	UserRoles.SILVER,
	UserRoles.GOLD,
	UserRoles.PLATINUM,
] as const;

const SUBSCRIPTION_LABELS: Record<(typeof SUBSCRIPTION_ROLES)[number], string> =
	{
		[UserRoles.BRONZE]: "Bronze Features",
		[UserRoles.SILVER]: "Silver Features",
		[UserRoles.GOLD]: "Gold Features",
		[UserRoles.PLATINUM]: "Platinum Features",
	};

const getWidgetsForRole = (role: UserRole, roles: UserRole[] | null) => {
	const allMenuItems = menuItems(roles);
	const roleWidgets = WIDGETS_BY_ROLE[role] || [];
	const widgets =
		role === "gold" ? [...roleWidgets, ...COMING_SOON_WIDGETS] : roleWidgets;

	return allMenuItems.filter((item) => widgets.includes(item.viewId));
};

export const MenuBar = () => {
	const { addMosaicWindow } = useMosaic();
	const { getTabLayout } = useTabs();
	const authContext = useContext(AuthContext);
	const roles = authContext?.roles || null;

	function handleOnSelect(item: MenuItem) {
		if (item.viewId === "Chat") {
			const tabLayout = getTabLayout();
			const isOpen = isWidgetOpened(tabLayout, "Chat");

			if (isOpen) return;
		}

		addMosaicWindow(item.viewId as ViewId);
	}

	const alreadyShownWidgets = new Set<ViewId>();

	return (
		<Menubar>
			<MenubarMenu>
				<MenubarTrigger className="p-2" data-testid="menubar">
					<svg
						aria-label="menu"
						role="img"
						fill="var(--gold)"
						className="w-5 h-5"
					>
						<use xlinkHref={`${sprites}#menu`} />
					</svg>
				</MenubarTrigger>

				<MenubarPortal>
					<MenubarContent
						data-testid="menubar-content"
						className="flex flex-col gap-3"
					>
						{SUBSCRIPTION_ROLES.map((role) => {
							const allWidgets = getWidgetsForRole(role, roles);

							const newWidgets = allWidgets.filter(
								(item) => !alreadyShownWidgets.has(item.viewId),
							);

							if (!newWidgets.length) return null;

							for (const newWidget of newWidgets) {
								alreadyShownWidgets.add(newWidget.viewId);
							}

							return (
								<div key={role} className="">
									<p className="uppercase text-light-gray text-sm font-medium mb-3">
										{SUBSCRIPTION_LABELS[role]}
									</p>
									<div className="border-l border-light-gray ml-0.5">
										{newWidgets.map((item) => (
											<HoverCardWrapper
												key={item.viewId}
												align="start"
												side="right"
												sideOffset={12}
												trigger={
													item.viewId === "Chat" ? (
														<ChatIconWithPhoneForm placing="menu" />
													) : (
														<MenubarItem
															key={item.viewId}
															data-testid={`menubar-item-${item.viewId}`}
															onSelect={() => handleOnSelect(item)}
															disabled={
																item?.disabled || !item?.isEnabledByUserRole
															}
														>
															<span
																className={cn(
																	"px-1 rounded transition-all duration-300",
																	getWidgetColorClassName(item.viewId),
																)}
															>
																{item.text}
															</span>
														</MenubarItem>
													)
												}
												content={
													!item?.disabled ? (
														<OfferToUpgrade
															offer={
																<p>
																	{mapOfferMessage[item.viewId] ||
																		"Please upgrade your plan"}
																</p>
															}
															buttonText={
																COMING_SOON_WIDGETS.includes(item.viewId)
																	? null
																	: "Upgrade Now"
															}
														/>
													) : null
												}
												disabled={item?.disabled || !item?.isEnabledByUserRole}
												className="bg-transparent"
											/>
										))}
									</div>
								</div>
							);
						})}
					</MenubarContent>
				</MenubarPortal>
			</MenubarMenu>
		</Menubar>
	);
};
